import {
  createContext,
  createElement,
  type ReactNode,
  useContext,
  useState,
} from 'react';
import { createStore, useStore } from 'zustand';
export enum SIGNAL_PAGE_VIEW_TYPE {
  LIST = 'list',
  MATRIX = 'matrix',
  CHART = 'chart',
}

interface SignalsPageState {
  // View state
  viewType: SIGNAL_PAGE_VIEW_TYPE;
  setViewType: (viewType: SIGNAL_PAGE_VIEW_TYPE) => void;

  // Sort state (for global data sorting via IssueSorter)
  sortStatus: NotNullableSortStatusT;
  setSortStatus: (sortStatus: NotNullableSortStatusT) => void;

  // Top count (for chart/matrix view)
  topCount: number;
  setTopCount: (topCount: number) => void;
}

type SignalsPageStore = ReturnType<typeof createSignalsPageStore>;
const SignalsPageContext = createContext<SignalsPageStore | null>(null);

const DEFAULT_SORT_STATUS: NotNullableSortStatusT = {
  sortBy: 'feedback',
  orderStatus: 'desc',
};

const createSignalsPageStore = () =>
  createStore<SignalsPageState>((set) => ({
    viewType: SIGNAL_PAGE_VIEW_TYPE.LIST,
    setViewType: (viewType) => set({ viewType }),

    sortStatus: DEFAULT_SORT_STATUS,
    setSortStatus: (sortStatus) => set({ sortStatus }),

    topCount: 5,
    setTopCount: (topCount) => set({ topCount }),
  }));

export function SignalsPageProvider({ children }: { children: ReactNode }) {
  const [store] = useState(() => createSignalsPageStore());
  return createElement(SignalsPageContext.Provider, { value: store }, children);
}

export function useSignalsPageStore<T>(
  selector: (state: SignalsPageState) => T,
): T {
  const store = useContext(SignalsPageContext);
  if (!store) {
    throw new Error(
      'useSignalsPageStore must be used within SignalsPageProvider',
    );
  }
  return useStore(store, selector);
}
